import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import {
  GeofenceAreaModel,
  useDeleteGeofenceAreaMutation,
  useListGeofenceAreasQuery,
} from '@casops/store/apis/geofenceAreasApi';
import { columnDefModelActions } from '@casops/components/tables/grid/columnDefs';
import { ModelActionsCellRendererParams } from '@casops/components/tables/cells/model-actions/ModelActionsCellRenderer';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { SidePanel } from '@casops/store/slices/globalSlice';
import { ModelName } from '@casops/store/api';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import { getDefaultModelArg } from '@casops/components/tables/cells/model-actions/utils';

type Model = GeofenceAreaModel;

const modelName = ModelName.GeofenceArea;
const sidePanel = SidePanel.ManageGeofenceArea;
const permission = Permission.GeofenceManagement;

const canDeleteItem = (data: Model): boolean => !data.used;

const getItemName = (data: Model): string => data.name;

const cellRendererParams: ModelActionsCellRendererParams<Model> = {
  modelName,
  permission,
  editProps: {
    sidePanel,
    canEditItem: true,
  },
  deleteProps: {
    canDeleteItem,
    getQueryArg: getDefaultModelArg,
    useDeleteModelMutation: useDeleteGeofenceAreaMutation,
    getItemName,
  },
};

export function GeofenceAreas() {
  const { t } = useTranslation();

  const isAllowedModelActions = useIsUserWithPermission(permission);

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      { field: 'name', headerName: t('Fields.AreaName'), minWidth: 100, maxWidth: 300 },
      { field: 'desc', headerName: t('Fields.Description'), minWidth: 300 },
    ];

    if (isAllowedModelActions) {
      columnDefs.push({
        ...columnDefModelActions<Model>(t, cellRendererParams),
        minWidth: 80,
        maxWidth: 120,
      });
    }

    return columnDefs;
  }, [t, isAllowedModelActions]);

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { data, ...requestRemainder } = useListGeofenceAreasQuery(undefined, {
    refetchOnMountOrArgChange: 120,
  });

  const { hasData, noDataDescription } = useTableData<Model>({
    data,
    ...requestRemainder,
    noResultsDescription,
  });

  return (
    <CollectionView<Model>
      sliceKey={modelName}
      modelName={modelName}
      columnDefs={columnDefs}
      rowData={data}
      hasData={hasData}
      noDataDescription={noDataDescription}
      actions={
        <CreateItemButton modelName={modelName} sidePanel={sidePanel} permission={permission} />
      }
    />
  );
}

export default GeofenceAreas;
